import {
  ACCOUNT_ORDER_CONFIRMATION,
  ACCOUNT_ORDER_CONFIRMATION_LOAD_MORE,
  ACCOUNT_ORDER_CONFIRMATION_SUBMIT,
} from './actions';
import {
  COMMON_SHOW_NOTIFICATION,
  ACCOUNT_OTP_VERIFY_SUCCESS,
  GET_SIGN_TOKENCA,
} from 'redux/actions';
import { IAction, INotification, IResponse } from 'interfaces/common';
import {
  IDerivativesOrderConfirmationResponse,
  IDerivativesOrderConfirmationSubmitParams,
  IDerivativesOrderConfirmationSubmitResponse,
  IEquityOrderConfirmationSubmitParams,
  IEquityOrderConfirmationSubmitResponse,
  IOrderConfirmationParams,
  IOrderConfirmationResponse,
  ISignTokenCAResponse,
  ISaveSignTokenCAResponse,
} from 'interfaces/api';
import {
  IOrderConfirmationAction,
  IOrderConfirmationSubmitAction,
} from 'interfaces/actions';
import { IOrderConfirmationSubmitReducer } from 'interfaces/reducers';
import { IState } from 'redux/global-reducers';
import { SystemType } from 'constants/enum';
import { ToastType } from 'react-toastify';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { query } from 'utils/socketApi';
import config from 'config';
import { signTokenCA, registerTokenCA } from '../../../../utils/signTokenCA';
import { addHours } from 'date-fns';

const queryEquityOrderConfirmation = (params: IOrderConfirmationParams) => {
  return query(config.apis.equityOrderConfirmation, params);
};

const queryEquityOrderConfirmationSubmit = (
  params: IEquityOrderConfirmationSubmitParams
) => {
  return query(config.apis.equityOrderConfirmationSubmit, params);
};

const queryDerivativesOrderConfirmation = (
  params: IOrderConfirmationParams
) => {
  return query(config.apis.derivativesOrderConfirmation, params);
};

const queryDerivativesOrderConfirmationSubmit = (
  params: IDerivativesOrderConfirmationSubmitParams
) => {
  return query(config.apis.derivativesOrderConfirmationSubmit, params);
};

function* doQueryOrderConfirmation(request: IAction<IOrderConfirmationAction>) {
  try {
    let responseData: IOrderConfirmationResponse[] | undefined;
    if (request.payload.systemType === SystemType.DERIVATIVES) {
      const response: IResponse<IDerivativesOrderConfirmationResponse> = yield call(
        queryDerivativesOrderConfirmation,
        request.payload
      );

      responseData = response.data.list;
    } else {
      const response: IResponse<IOrderConfirmationResponse[]> = yield call(
        queryEquityOrderConfirmation,
        request.payload
      );
      responseData = response.data;
    }

    if (request.response) {
      yield put<IAction<IOrderConfirmationResponse[]>>({
        type: request.response.success,
        payload: responseData,
      });
    }
  } catch (error) {
    console.error('Order Confirmation', error);
    if (request.response) {
      yield put<IAction<string>>({
        type: request.response.failed,
        payload: error.code || error.message,
      });
    }

    yield put<IAction<INotification>>({
      type: COMMON_SHOW_NOTIFICATION,
      payload: {
        type: ToastType.ERROR,
        title: 'Order Confirmation',
        content: error.code || error.message,
        contentParams: error.messageParams,
        time: new Date(),
      },
    });
  }
}

function* doQueryOrderConfirmationSubmit(
  request: IAction<IOrderConfirmationSubmitAction>
) {
  const store: IState = yield select((state: IState) => ({
    lang: state.lang,
    signTokenCA: state.signTokenCA,
    otp: state.otp,
  }));

  try {
    let responseData: IOrderConfirmationSubmitReducer | undefined;

    const drOrderConfirmParams = {
      accountNo: request.payload.accountNumber,
      language: store.lang,
      details: request.payload.details.map((val) => ({
        isHistory: val.isHistory,
        orderGroupId: val.orderGroupId ?? '',
        refId: val.refId ?? '',
      })),
    };
    const eqOrderConfirmParams = {
      accountNo: request.payload.accountNumber,
      mvOrderList: request.payload.details.map((val) => [
        val.orderGroupId ?? '',
        val.isHistory,
        val.refId ?? '',
      ]),
      macAddress: request.payload.macAddress,
    };
    if (store.signTokenCA?.isTokenCA && store.signTokenCA?.serial) {
      const data = {
        action: 'verifyTokenOrderConfirmation',
        serialToken: store.signTokenCA?.serial,
        dataSign: {
          data: request.payload,
        },
        timeSign: new Date(),
      };

      const responseSign: ISignTokenCAResponse = yield call(
        signTokenCA,
        store.signTokenCA?.serial,
        data
      );
      if (responseSign && responseSign.code == 1) {
        let dataSubmit = {
          dataSignTokenCA: responseSign.data,
          ...data,
        };

        const responseSaveSignTokenCA: ISaveSignTokenCAResponse = yield call(
          registerTokenCA,
          {
            ...data,
            dataSign: JSON.stringify(dataSubmit),
            timeSign: addHours(new Date(), 7).toISOString() ?? '',
          }
        );

        if (responseSaveSignTokenCA && responseSaveSignTokenCA.status !== 201) {
          yield put<IAction<INotification>>({
            type: COMMON_SHOW_NOTIFICATION,
            payload: {
              type: ToastType.ERROR,
              title: 'Order',
              content: 'ORDER_FAILED',
              time: new Date(),
            },
          });
        } else {
          if (request.payload.systemType === SystemType.DERIVATIVES) {
            const response: IResponse<IDerivativesOrderConfirmationSubmitResponse> = yield call(
              queryDerivativesOrderConfirmationSubmit,
              drOrderConfirmParams
            );
            responseData = response.data;
          } else {
            const response: IResponse<IEquityOrderConfirmationSubmitResponse> = yield call(
              queryEquityOrderConfirmationSubmit,
              eqOrderConfirmParams
            );
            responseData = response.data;
          }
          yield put<IAction<INotification>>({
            type: COMMON_SHOW_NOTIFICATION,
            payload: {
              type: ToastType.SUCCESS,
              title: 'Order Confirmation',
              content: responseData?.message,
              time: new Date(),
            },
          });
          if (request.response) {
            yield put<IAction<IOrderConfirmationSubmitReducer>>({
              type: request.response.success,
              payload: responseData,
            });
          }
        }
      }
    } else {
      if (request.payload.systemType === SystemType.DERIVATIVES) {
        const response: IResponse<IDerivativesOrderConfirmationSubmitResponse> = yield call(
          queryDerivativesOrderConfirmationSubmit,
          drOrderConfirmParams
        );
        responseData = response.data;
      } else {
        const response: IResponse<IEquityOrderConfirmationSubmitResponse> = yield call(
          queryEquityOrderConfirmationSubmit,
          eqOrderConfirmParams
        );

        responseData = response.data;
      }

      yield put<IAction<INotification>>({
        type: COMMON_SHOW_NOTIFICATION,
        payload: {
          type: ToastType.SUCCESS,
          title: 'Order Confirmation',
          content: responseData?.message,
          time: new Date(),
        },
      });

      if (request.response) {
        yield put<IAction<IOrderConfirmationSubmitReducer>>({
          type: request.response.success,
          payload: responseData,
        });
      }
    }
  } catch (error) {
    console.error('Order Confirmation submit', error);
    if (request.response) {
      yield put<IAction<string>>({
        type: request.response.failed,
        payload: error.code || error.message,
      });
    }

    yield put<IAction<INotification>>({
      type: COMMON_SHOW_NOTIFICATION,
      payload: {
        type: ToastType.ERROR,
        title: 'Order Confirmation',
        content: error.code || error.message,
        contentParams: error.messageParams,
        time: new Date(),
      },
    });
  }

  if (store.otp?.otpToken?.data?.expiryTime == 0) {
    yield put({
      type: ACCOUNT_OTP_VERIFY_SUCCESS,
      payload: {
        token: null,
      },
    });
  }

  yield put({
    type: GET_SIGN_TOKENCA,
    payload: {
      serial: '',
      isTokenCA: false,
    },
  });
}

export function* watchQueryOrderConfirmation() {
  yield takeLatest(ACCOUNT_ORDER_CONFIRMATION, doQueryOrderConfirmation);
  yield takeLatest(
    ACCOUNT_ORDER_CONFIRMATION_LOAD_MORE,
    doQueryOrderConfirmation
  );
  yield takeLatest(
    ACCOUNT_ORDER_CONFIRMATION_SUBMIT,
    doQueryOrderConfirmationSubmit
  );
}



// WEBPACK FOOTER //
// ./src/components/areas/Account/OrderConfirmation/sagas.ts